import { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { formatRupiah } from '../../types'
import { orderApi } from '../../utils/api'

interface Order {
  id: number
  orderNumber: string
  outletName: string
  totalAmount: number
  status: string
  createdAt: string
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Dikonfirmasi', className: 'badge-info' },
  PRINTING: { label: 'Sedang Cetak', className: 'badge-warning' },
  PRINTED: { label: 'Tercetak', className: 'badge-success' },
  PRINT_FAILED: { label: 'Gagal Cetak', className: 'badge-error' },
}

export function AdminOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await orderApi.getAll()
      setOrders(res.data as Order[])
    } catch (err) {
      console.error('Failed to fetch orders:', err)
      setError('Gagal memuat data pesanan')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" aria-label="Memuat pesanan" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-state">
        <svg className="error-icon w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h2 className="error-title">Gagal Memuat Pesanan</h2>
        <p className="error-message">{error}</p>
        <button onClick={fetchOrders} className="btn-primary">
          Coba Lagi
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Pesanan</h1>
        <p className="text-slate-500 mt-1">Kelola dan lihat pesanan pelanggan</p>
      </div>

      <div className="card">
        {orders.length === 0 ? (
          <div className="empty-state py-16">
            <svg className="empty-icon w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Pesanan</h2>
            <p className="empty-text">Tidak ada pesanan yang telah tercatat dalam sistem.</p>
            <p className="empty-hint mt-2">
              Pesanan akan muncul di sini setelah pelanggan membuat pemesanan melalui halaman /order.
            </p>
            <div className="mt-6">
              <NavLink to="/admin/dashboard" className="btn-primary">
                ke Dashboard
              </NavLink>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">No. Pesanan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Waktu</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nama Outlet</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-4 py-3 font-mono text-sm text-slate-900">{order.orderNumber}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {new Date(order.createdAt).toLocaleString('id-ID', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">{order.outletName}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium">{formatRupiah(order.totalAmount)}</td>
                      <td className="px-4 py-3">
                        <span className={`badge ${STATUS_CONFIG[order.status]?.className || 'badge-info'}`}>
                          {STATUS_CONFIG[order.status]?.label || order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <NavLink
                          to={`/admin/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Lihat Detail
                        </NavLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-200 text-sm text-slate-500">
              Menampilkan {orders.length} pesanan
            </div>
          </>
        )}
      </div>
    </div>
  )
}